import { ApolloClient, InMemoryCache, NormalizedCacheObject, split } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectToken } from '../redux/selectors';
import { httpLink } from './apollo/httplink';
import { wsLink } from './apollo/websocketlink';

// The split function takes three parameters:
//
// * A function that's called for each operation to execute
// * The Link to use for an operation if the function returns a "truthy" value
// * The Link to use for an operation if the function returns a "falsy" value
const splitLink = ({ token }: { token: string }) =>
  split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink({ token }),
    httpLink({ token })
  );

export function createClient({ token }: { token: string }): ApolloClient<NormalizedCacheObject> {
  if (token) {
    return new ApolloClient({
      link: splitLink({ token }),
      cache: new InMemoryCache(),
    });
  }

  return createHttpClient({ token });
}

export function createHttpClient({ token }: { token: string }): ApolloClient<NormalizedCacheObject> {
  return new ApolloClient({
    link: httpLink({ token }),
    cache: new InMemoryCache(),
  });
}

export function useAppApolloClient<S extends boolean>(x?: S): S extends false ? ApolloClient<NormalizedCacheObject> : undefined;
export function useAppApolloClient(skip: boolean = false): ApolloClient<NormalizedCacheObject> | undefined {
  const token = useSelector(selectToken);
  const apolloInstance = useMemo(() => {
    if (skip) return undefined;
    return createClient({ token });
  }, [skip, token]);
  return apolloInstance;
}
